import React, { useState, useEffect } from 'react';
import Switch from 'react-switch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';

// URL del formulario de HubSpot (nuevo)
const HUBSPOT_FORM_URL =
  'https://tdgrm.share.hsforms.com/2FAjL4sl2RxWQRKUVFiD8Dg';

// 1) Datos de precio y límite de usuarios
const basePlans = {
  BASIC: {
    priceMonthly: '30€',
    originalPriceMonthly: '30€',
    priceYearly: '25€',
    originalPriceYearly: '30€',
    credits: '1 user',
  },
  PREMIUM: {
    priceMonthly: '108€',
    originalPriceMonthly: '108€',
    priceYearly: '90€',
    originalPriceYearly: '108€',
    credits: 'Up to 20 users',
  },
  BUSINESS: {
    priceMonthly: '300€',
    originalPriceMonthly: '300€',
    priceYearly: '208,3€',
    originalPriceYearly: '300€',
    credits: 'Up to 100 users',
  },
  ENTERPRISE: {
    priceMonthly: 'A medida',
    originalPriceMonthly: null,
    priceYearly: 'A medida',
    originalPriceYearly: null,
    credits: 'Unlimited',
  },
};

// 2) Traducciones
const translations = {
  es: {
    title: 'Precios',
    subtitle:
      'Desde una persona hasta una gran empresa, tenemos un plan para ti.',
    footnote: '*Precios sin IVA',
    plans: {
      BASIC: {
        name: 'PRO',
        description:
          'Para los que quieren herramientas de desarrollo personalizadas',
        ...basePlans.BASIC,
      },
      PREMIUM: {
        name: 'TEAM',
        description: 'Para los equipos exigentes de alto rendimiento',
        ...basePlans.PREMIUM,
      },
      BUSINESS: {
        name: 'BUSINESS',
        description: 'Para empresas medianas que se preocupan por su talento',
        popular: true,
        ...basePlans.BUSINESS,
      },
      ENTERPRISE: {
        name: 'ENTERPRISE',
        description:
          'Para que tus empleados puedan ser mucho más que un número',
        ...basePlans.ENTERPRISE,
      },
    },
  },
  en: {
    title: 'Pricing',
    subtitle:
      'From one individual to one large enterprise, we have a plan for you.',
    footnote: '*Prices do not include VAT',
    plans: {
      BASIC: {
        name: 'PRO',
        description:
          'For those who want personalized personal development tools.',
        ...basePlans.BASIC,
      },
      PREMIUM: {
        name: 'TEAM',
        description: 'For high-performance teams with high standards.',
        ...basePlans.PREMIUM,
      },
      BUSINESS: {
        name: 'BUSINESS',
        description: 'For medium-sized companies that care about their talent.',
        popular: true,
        ...basePlans.BUSINESS,
      },
      ENTERPRISE: {
        name: 'ENTERPRISE',
        description: 'So your employees can be much more than just a number.',
        ...basePlans.ENTERPRISE,
      },
    },
  },
};

export default function Pricing({ language = 'es', onContactClick }) {
  const [isAnnual, setIsAnnual] = useState(true);
  const { title, subtitle, footnote, plans } =
    translations[language] || translations.es;
  const planKeys = Object.keys(plans);

  // Si cambias de idioma, resetear a mensual
  useEffect(() => {
    setIsAnnual(false);
  }, [language]);

  // Abrir HubSpot (o usar handler del padre si se proporciona)
  const handleContactClick = () => {
    if (typeof onContactClick === 'function') {
      onContactClick();
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(HUBSPOT_FORM_URL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section style={styles.section}>
      <h2 style={styles.title}>{title}</h2>
      <p style={styles.subtitle}>{subtitle}</p>

      {/* Toggle MENSUAL / ANUAL */}
      <div style={styles.toggleContainer}>
        <span style={styles.toggleLabel}>
          {language === 'es' ? 'MENSUAL' : 'MONTHLY'}
        </span>
        <Switch
          checked={isAnnual}
          onChange={() => setIsAnnual(!isAnnual)}
          onColor="#4CAF50"
          offColor="#ccc"
          onHandleColor="#fff"
          offHandleColor="#fff"
          checkedIcon={false}
          uncheckedIcon={false}
          handleDiameter={22}
          height={28}
          width={50}
        />
        <span style={styles.toggleLabel}>
          {language === 'es' ? 'ANUAL' : 'YEARLY'}
        </span>
        <span style={styles.discountLabel}>
          {language === 'es' ? '2 MESES GRATIS' : '2 MONTHS FREE'}
        </span>
      </div>

      {/* Plan Cards */}
      <div style={styles.plansContainer}>
        {planKeys.map((key) => {
          const plan = plans[key];
          const price = isAnnual ? plan.priceYearly : plan.priceMonthly;
          const original = isAnnual
            ? plan.originalPriceYearly
            : plan.originalPriceMonthly;
          const isEnt = plan.name === 'ENTERPRISE';

          return (
            <div
              key={key}
              style={{
                ...styles.planCard,
                ...(plan.popular ? styles.popularPlan : {}),
              }}
            >
              {plan.popular && (
                <div style={styles.popularTag}>
                  {language === 'es' ? 'MÁS POPULAR' : 'MOST POPULAR'}
                </div>
              )}
              <h3 style={styles.planTitle}>{plan.name}</h3>
              <p style={styles.planDescription}>{plan.description}</p>
              <div style={styles.priceSection}>
                {original && original !== price ? (
                  <p style={styles.originalPrice}>{original}</p>
                ) : (
                  <p style={styles.emptyLine} />
                )}
                {!isEnt ? (
                  <p style={styles.planPrice}>
                    {price}
                    <span style={styles.priceSuffix}>
                      {language === 'es' ? '/mes' : '/month'}
                    </span>
                  </p>
                ) : (
                  <p style={styles.planPrice}>
                    <FontAwesomeIcon icon={faEnvelopeOpenText} />
                  </p>
                )}
                {isAnnual && !isEnt ? (
                  <p style={styles.billedText}>
                    {language === 'es'
                      ? 'Facturado anualmente'
                      : 'Billed yearly'}
                  </p>
                ) : (
                  <p style={styles.emptyLine} />
                )}
              </div>

              {/* Créditos en la base de la card */}
              <p style={styles.credits}>{plan.credits}</p>
            </div>
          );
        })}
      </div>

      {/* Footnote pegado a las cards */}
      <p style={styles.footnote}>{footnote}</p>

      {/* Botón único de Contacto */}
      <div style={styles.contactWrapper}>
        <button onClick={handleContactClick} style={styles.contactButton}>
          <FontAwesomeIcon
            icon={faEnvelopeOpenText}
            style={{ marginRight: '8px' }}
          />
          {language === 'es' ? 'Contáctanos' : 'Contact us'}
        </button>
      </div>
    </section>
  );
}

const styles = {
  section: {
    padding: '50px 20px',
    backgroundColor: '#fff',
  },
  title: {
    fontSize: '36px',
    fontWeight: 600,
    color: '#1C1C3B',
    margin: '40px 0 10px 50px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '18px',
    fontWeight: 500,
    color: '#555',
    margin: '0 0 40px 55px',
    textAlign: 'center',
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
    paddingRight: '45px',
    marginBottom: '20px',
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1C1C3B',
  },
  discountLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#4CAF50',
    backgroundColor: '#E8F5E9',
    padding: '6px',
    borderRadius: '15px',
  },
  plansContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '10px',
  },
  planCard: {
    width: '275px',
    padding: '40px',
    paddingBottom: '60px',
    borderRadius: '10px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    backgroundColor: '#fff',
    minHeight: '380px',
  },
  popularPlan: {
    border: '2px solid #ff2300',
  },
  popularTag: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ff2300',
    color: '#fff',
    padding: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
    borderRadius: '8px 8px 0 0',
  },
  planTitle: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#1C1C3B',
    marginBottom: '5px',
  },
  planDescription: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#555',
    textAlign: 'center',
    minHeight: '40px',
    margin: '10px 0 20px',
  },
  priceSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '10px',
    minHeight: '80px',
  },
  emptyLine: {
    height: '0.8em',
    margin: 0,
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: '#888',
    fontSize: '16px',
    margin: 0,
    fontWeight: 500,
  },
  planPrice: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#1C1C3B',
    margin: 0,
  },
  priceSuffix: {
    fontSize: '18px',
    fontWeight: 500,
    marginLeft: '4px',
    color: '#555',
  },
  billedText: {
    fontSize: '14px',
    fontWeight: 300,
    color: '#555',
    margin: 0,
    marginBottom: '20px',
  },
  credits: {
    position: 'absolute',
    bottom: '20px',
    left: 0,
    width: '100%',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 400,
    color: '#555',
  },
  footnote: {
    fontSize: '12px',
    fontWeight: 300,
    color: '#888',
    textAlign: 'center',
    margin: '10px 0',
  },
  contactWrapper: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  contactButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 700,
    backgroundColor: '#ff2300',
    color: '#fff',
    border: 'none',
    borderRadius: '30px',
    cursor: 'pointer',
  },
};
